<template>
  <div class="welcome-page" :lang="$locale === 'en' ? 'en' : 'zh-CN'" @click="dismissContact" @keydown.esc="closeContact">
    <div class="welcome-scroll-progress" aria-hidden="true" />
    <header>
      <div class="logo-box"><div class="logo-icon">L</div><div class="logo-text">Label<span>Cheap</span></div></div>
      <div class="welcome-nav-actions"><button class="welcome-language" type="button" :aria-label="copy.languageLabel" @click="$setLocale($locale === 'en' ? 'zh' : 'en')">{{ copy.lang }}</button><a :href="loginUrl" class="nav-btn">{{ copy.login }}</a></div>
    </header>
    <section class="hero">
      <h1>{{ copy.heroBefore }}<br><span>{{ copy.heroAccent }}</span>{{ copy.heroAfter }}</h1>
      <p class="sub">{{ copy.heroIntro }}</p>
      <div class="hero-cta"><a :href="loginUrl" class="btn-main">{{ copy.startOrder }}</a><span class="alert-text">{{ copy.fraudAlert }}</span></div>
    </section>
    <section class="overview">
      <div class="overview-container">
        <div class="waybill-card"><div class="waybill-img" aria-hidden="true"><span class="parcel-float">📦</span></div><div class="tags"><span v-for="tag in copy.tags" :key="tag" class="tag">{{ tag }}</span></div></div>
        <div class="data-grid"><div v-for="(item, i) in copy.highlights" :key="item.value" class="data-item"><div class="data-num" :class="i % 2 ? 'yellow' : 'dark'">{{ item.value }}<span v-if="item.unit" style="font-size:24px">{{ item.unit }}</span></div><div class="data-desc">{{ item.lines[0] }}<br>{{ item.lines[1] }}</div></div></div>
      </div>
      <div class="disclaimer">{{ copy.disclaimer }}</div>
    </section>
    <section class="features">
      <div class="feat-head"><h2>{{ copy.featuresTitle }}</h2></div>
      <div v-for="feature in copy.features" :key="feature.icon" class="feat-card">
        <div class="feat-visual" aria-hidden="true">{{ feature.icon }}</div>
        <div class="feat-text"><h3>{{ feature.before }}<span>{{ feature.accent }}</span>{{ feature.after }}</h3><p>{{ feature.intro }}</p><ul class="feat-list"><li v-for="point in feature.points" :key="point.text"><strong v-if="point.alert" style="color:var(--red)">{{ point.text }}</strong><template v-else>{{ point.text }}</template></li></ul></div>
      </div>
      <div class="alert-box"><div><h4>{{ copy.statementTitle }}</h4><p style="margin-top:10px;color:#cbd5e1">{{ copy.statementText }}</p></div></div>
    </section>
    <section class="bottom-cta"><h2>{{ copy.heroBefore }}<span>{{ copy.heroAccent }}</span>{{ copy.closingAfter }}</h2><p style="font-size:20px;color:#94a3b8">{{ copy.closingIntro }}</p><a :href="loginUrl" class="btn-light">{{ copy.startShipping }}</a></section>
    <footer>{{ copy.footer }}</footer>
    <aside id="contact" class="welcome-support-rail" @click.stop :class="{ 'is-open': contactOpen }" :aria-label="copy.contact">
      <button ref="contactTrigger" class="welcome-support-trigger" type="button" :aria-label="copy.contact" :aria-expanded="String(contactOpen)" aria-controls="welcome-support-panel" @click="contactOpen = !contactOpen"><i class="el-icon-service" aria-hidden="true" /><span>{{ copy.contactShort }}</span></button>
      <section id="welcome-support-panel" class="welcome-support-panel">
        <div class="welcome-support-title"><span class="welcome-support-symbol"><i class="el-icon-service" aria-hidden="true" /></span><strong>{{ copy.contact }}</strong><button ref="contactClose" type="button" class="welcome-support-close" :aria-label="copy.closeContact" @click="closeContact"><i class="el-icon-close" aria-hidden="true" /></button></div>
        <div class="welcome-support-body">
          <p class="welcome-support-hint">{{ copy.contactHint }}</p>
          <div v-for="contact in contacts" :key="contact.region" class="welcome-support-phone">
            <div><span class="support-country">{{ contact.region }}</span><strong>{{ contact.region === 'US' ? copy.us : copy.cn }}</strong></div>
            <a v-if="contact.type === 'email'" :href="'mailto:' + contact.value" class="support-number">{{ contact.value }}</a>
            <b v-else class="support-number">{{ contact.value }}</b>
            <div class="support-phone-actions"><small>{{ copy.contactType[contact.type] }}</small><button type="button" :aria-label="copy.copyContact[contact.type] + ' ' + contact.value" @click="copyContact(contact)"><i :class="copiedRegion === contact.region ? 'el-icon-check' : 'el-icon-document-copy'" aria-hidden="true" />{{ copiedRegion === contact.region ? copy.copied : copy.copyContact[contact.type] }}</button></div>
          </div>
          <span v-if="copyError" class="support-copy-error" role="status">{{ copy.copyFailed }}</span><span class="welcome-sr-only" role="status" aria-live="polite">{{ copiedRegion ? copy.copied : '' }}</span>
        </div>
        <button class="welcome-support-top" type="button" @click="scrollTo('top')">{{ copy.top }} <i class="el-icon-top" aria-hidden="true" /></button>
      </section>
    </aside>
  </div>
</template>

<script>
import { SERVICE_CONTACTS, WELCOME_CONTENT } from './welcomeContent'
import { setupWelcomeEffects } from './welcomeEffects'

export default {
  name: 'Welcome',
  data () { return { contacts: SERVICE_CONTACTS, contactOpen: false, copiedRegion: '', copyError: false, copyTimer: null } },
  computed: {
    copy () { return WELCOME_CONTENT[this.$locale === 'en' ? 'en' : 'zh'] },
    loginUrl () { return /^(www\.)?admin\.labelcheap\.com$/i.test(window.location.hostname) ? 'https://www.labelcheap.com/portal/sign-in' : '/portal/sign-in' }
  },
  watch: { $locale: 'updateTitle' },
  mounted () { this.updateTitle(); this.contactOpen = false; this._stopWelcomeEffects = setupWelcomeEffects(this.$el) },
  beforeDestroy () { if (this.copyTimer) window.clearTimeout(this.copyTimer); if (this._stopWelcomeEffects) this._stopWelcomeEffects() },
  methods: {
    updateTitle () { document.title = this.$locale === 'en' ? 'LabelCheap · Real labels, weekly billing' : 'LabelCheap · 正规单源，一分钟出单' },
    async copyContact (contact) {
      this.copyError = false
      this.copiedRegion = ''
      try {
        if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(contact.value)
        else {
          const input = document.createElement('textarea')
          input.value = contact.value; input.setAttribute('readonly', ''); input.style.cssText = 'position:fixed;left:-9999px;top:0'
          document.body.appendChild(input)
          try { input.select(); if (!document.execCommand('copy')) throw new Error('copy failed') } finally { input.remove() }
        }
        this.copiedRegion = contact.region
        if (this.copyTimer) window.clearTimeout(this.copyTimer)
        this.copyTimer = window.setTimeout(() => { this.copiedRegion = '' }, 2200)
      } catch (error) { this.copyError = true }
    },
    dismissContact () { if (this.contactOpen) this.closeContact(false) },
    closeContact (restoreFocus = true) { this.contactOpen = false; if (restoreFocus) this.$nextTick(() => { if (this.$refs.contactTrigger) this.$refs.contactTrigger.focus() }) },
    openContact () { this.contactOpen = true; this.$nextTick(() => { if (window.matchMedia('(max-width: 1180px)').matches && this.$refs.contactClose) this.$refs.contactClose.focus() }) },
    scrollTo (id) {
      const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
      if (id === 'top') { this.$el.scrollTo({ top: 0, behavior }); return }
      const element = this.$el.querySelector('#' + id)
      if (element) element.scrollIntoView({ behavior, block: 'start' })
    }
  }
}
</script>
<style scoped>

  .welcome-page {
    --yellow: #F4B400;
    --dark: #0F172A;
    --red: #E11D48;
    --gray-bg: #F8FAFC;
    --text-gray: #64748B;
  }

  * { margin: 0; padding: 0; box-sizing: border-box; }
  .welcome-page {
    font-family: -apple-system, BlinkMacSystemFont, "Helvetica Neue", "PingFang SC", "Microsoft YaHei", sans-serif;
    background: var(--gray-bg);
    color: var(--dark);
    line-height: 1.6;
    overflow-x: hidden;
  }

  /* 导航 */
  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 5%;
    background: #fff;
    position: sticky;
    top: 0;
    z-index: 100;
    border-bottom: 1px solid rgba(0,0,0,0.05);
  }
  .logo-box {
    display: flex;
    align-items: center;
    gap: 12px;
  }
  .logo-icon {
    width: 48px;
    height: 48px;
    background: var(--yellow);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--dark);
    font-weight: 900;
    font-size: 32px;
    transform: rotate(-5deg);
  }
  .logo-text {
    font-size: 24px;
    font-weight: 800;
    letter-spacing: -1px;
    color: var(--dark);
  }
  .logo-text span { color: var(--yellow); }
  .nav-btn {
    padding: 10px 24px;
    background: var(--dark);
    color: #fff;
    border-radius: 30px;
    text-decoration: none;
    font-weight: 600;
    transition: 0.3s;
  }
  .nav-btn:hover { background: var(--yellow); color: var(--dark); }

  /* 主视觉 Hero */
  .hero {
    padding: 100px 5% 80px;
    text-align: center;
    background: #fff;
    position: relative;
    overflow: hidden;
  }
  .hero::before {
    content: "";
    position: absolute;
    top: -50%;
    left: -10%;
    width: 500px;
    height: 500px;
    background: rgba(244, 180, 0, 0.1);
    border-radius: 50%;
    z-index: 0;
  }
  .hero h1 {
    font-size: clamp(40px, 6vw, 72px);
    font-weight: 900;
    line-height: 1.1;
    margin-bottom: 24px;
    position: relative;
    z-index: 1;
    letter-spacing: -2px;
  }
  .hero h1 span { color: var(--yellow); }
  .hero h1, .bottom-cta h2 { overflow-wrap: anywhere; }
  .hero p.sub {
    font-size: 20px;
    color: var(--text-gray);
    max-width: 700px;
    margin: 0 auto 40px;
    position: relative;
    z-index: 1;
  }
  .hero-cta {
    display: inline-flex;
    align-items: center;
    gap: 16px;
    position: relative;
    z-index: 1;
  }
  .btn-main {
    padding: 18px 40px;
    background: var(--dark);
    color: #fff;
    font-size: 18px;
    font-weight: 700;
    border-radius: 40px;
    text-decoration: none;
    transition: 0.3s;
    cursor: pointer;
    border: none;
  }
  .btn-main:hover {
    background: var(--yellow);
    color: var(--dark);
    transform: translateY(-2px);
    box-shadow: 0 10px 20px rgba(244,180,0,0.3);
  }
  .alert-text {
    color: var(--red);
    font-weight: 800;
    font-size: 16px;
  }

  /* 图三模块：简介数据区 */
  .overview {
    padding: 80px 5%;
    background: var(--gray-bg);
  }
  .overview-container {
    max-width: 1200px;
    margin: 0 auto;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 40px;
    align-items: center;
  }
  .waybill-card {
    background: #fff;
    padding: 40px;
    border-radius: 24px;
    box-shadow: 0 20px 40px rgba(0,0,0,0.04);
    position: relative;
  }
  .waybill-img {
    width: 100%;
    aspect-ratio: 4/3;
    background: #f1f5f9;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 48px;
    margin-bottom: 24px;
    border: 1px dashed #cbd5e1;
  }
  .tags { display: flex; flex-wrap: wrap; gap: 10px; }
  .tag {
    padding: 6px 14px;
    border: 1px solid #e2e8f0;
    border-radius: 20px;
    font-size: 13px;
    font-weight: 600;
    color: var(--text-gray);
  }
  .data-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }
  .data-item {
    background: #fff;
    padding: 32px;
    border-radius: 24px;
    box-shadow: 0 20px 40px rgba(0,0,0,0.04);
  }
  .data-num {
    font-size: 56px;
    font-weight: 900;
    line-height: 1;
    margin-bottom: 12px;
    letter-spacing: -2px;
  }
  .data-num.yellow { color: var(--yellow); }
  .data-num.dark { color: var(--dark); }
  .data-desc {
    font-size: 15px;
    color: var(--text-gray);
    font-weight: 500;
  }
  .disclaimer {
    max-width: 1200px;
    margin: 24px auto 0;
    font-size: 12px;
    color: #94a3b8;
    text-align: left;
  }

  /* 四大细化模块 */
  .features {
    padding: 100px 5%;
    background: #fff;
  }
  .feat-head {
    text-align: center;
    margin-bottom: 80px;
  }
  .feat-head h2 {
    font-size: 44px;
    font-weight: 900;
    letter-spacing: -1px;
  }
  .feat-card {
    max-width: 1200px;
    margin: 0 auto 40px;
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 60px;
    align-items: center;
    padding: 40px;
    border-radius: 32px;
    transition: 0.4s;
  }
  .feat-card:hover {
    background: var(--gray-bg);
    transform: translateY(-5px);
  }
  .feat-card:nth-child(even) {
    direction: rtl;
  }
  .feat-card:nth-child(even) > * {
    direction: ltr;
  }
  .feat-visual {
    font-size: 80px;
    text-align: center;
    background: #fff;
    padding: 40px;
    border-radius: 24px;
    box-shadow: 0 10px 30px rgba(0,0,0,0.03);
  }
  .feat-text h3 {
    font-size: 36px;
    font-weight: 900;
    margin-bottom: 16px;
    line-height: 1.2;
  }
  .feat-text h3 span { color: var(--yellow); }
  .feat-text p {
    font-size: 18px;
    color: var(--text-gray);
    margin-bottom: 24px;
  }
  .feat-list {
    list-style: none;
  }
  .feat-list li {
    padding: 10px 0;
    font-size: 16px;
    font-weight: 500;
    border-bottom: 1px solid rgba(0,0,0,0.05);
  }
  .feat-list li:last-child { border: none; }

  /* 警示爆点 */
  .alert-box {
    background: var(--dark);
    color: #fff;
    padding: 40px;
    border-radius: 32px;
    margin: 60px auto;
    max-width: 1200px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 20px;
    border-left: 10px solid var(--red);
  }
  .alert-box h4 {
    font-size: 28px;
    font-weight: 900;
    color: var(--yellow);
  }

  /* 底部 */
  .bottom-cta {
    padding: 100px 5%;
    text-align: center;
    background: var(--dark);
    color: #fff;
  }
  .bottom-cta h2 {
    font-size: 56px;
    font-weight: 900;
    margin-bottom: 24px;
  }
  .bottom-cta h2 span { color: var(--yellow); }
  .btn-light {
    padding: 18px 48px;
    background: var(--yellow);
    color: var(--dark);
    font-size: 18px;
    font-weight: 800;
    border-radius: 40px;
    text-decoration: none;
    display: inline-block;
    margin-top: 20px;
  }

  footer {
    padding: 40px 5%;
    text-align: center;
    background: #020617;
    color: #64748b;
    font-size: 14px;
  }

  @media (max-width: 768px) {
    .overview-container, .data-grid, .feat-card { grid-template-columns: 1fr; }
    .feat-card:nth-child(even) { direction: ltr; }
    .alert-box { flex-direction: column; text-align: center; }
    .hero h1 { font-size: 40px; }
  }

.welcome-page { height: var(--app-viewport-height, 100dvh); overflow-y: auto; font-size: 16px; }

.welcome-sr-only { position: absolute; width: 1px; height: 1px; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; }

.welcome-support-rail { position: fixed; z-index: 130; right: 20px; top: 50%; transform: translateY(-50%); width: 206px; }
.welcome-support-trigger { display: none; }
.welcome-support-panel { display: none; background: #fff; border: 1px solid #e2e5e9; border-radius: 10px; max-height: calc(100dvh - 90px); overflow: auto; box-shadow: 0 8px 26px rgba(24,33,48,.06); }
.welcome-support-rail.is-open .welcome-support-panel { display: block; }
.welcome-support-title { display: flex; align-items: center; gap: 8px; padding: 17px 15px; background: #1d2839; color: #fff; }
.welcome-support-title strong { font-size: 14px; font-weight: 500; }
.welcome-support-symbol { display: flex; align-items: center; justify-content: center; width: 25px; color: #edbd46; font-size: 20px; }
.welcome-support-close { display: block; margin-left: auto; padding: 4px 0; background: transparent; border: 0; color: #9ba7b8; font-size: 14px !important; }
.welcome-support-body { padding: 0 15px; }
.welcome-support-hint { margin: 13px 0 17px; font-size: 11px; color: #7d8794; }
.welcome-support-phone { margin-bottom: 13px; padding-bottom: 13px; border-bottom: 1px solid #edf0f3; }
.welcome-support-phone > div:first-child { display: flex; align-items: center; gap: 7px; }
.welcome-support-phone strong { font-size: 11px; font-weight: 400; color: #697587; }
.support-country { padding: 0 4px; border: 1px solid #e2e6eb; border-radius: 3px; color: #9ba5b2; font: 8px/1.7 monospace; }
.support-number { display: block; margin: 8px 0 4px; color: #536072 !important; font-size: 15px; font-weight: 400; letter-spacing: -.3px; white-space: nowrap; font-variant-numeric: tabular-nums; }
.support-phone-actions { display: flex; justify-content: space-between; align-items: center; gap: 6px; }
.support-phone-actions small { color: #a1aab6; font-size: 8px; }
.support-phone-actions button { min-height: 28px; padding: 2px 0; border: 0; background: transparent; color: #8b94a1; font-size: 10px !important; }
.support-phone-actions button i { margin-right: 3px; }
.support-phone-actions button:hover { color: #9b7520; }
.welcome-contact-note { margin: 0 0 14px; color: #9ba5b2; font-size: 10px; line-height: 1.8; }
.support-copy-error { display: block; margin-bottom: 10px; color: #ab6660; font-size: 10px; }
.welcome-support-top { width: 100%; padding: 9px 12px; border: 0; border-top: 1px solid #edf0f3; background: #f8f9fb; color: #9ba5b2; font-size: 9px !important; }
.welcome-support-rail:not(.is-open) { width: 46px; }
.welcome-support-rail:not(.is-open) .welcome-support-trigger { display: flex; }
.welcome-support-trigger { flex-direction: column; align-items: center; gap: 7px; width: 46px; padding: 14px 7px; border: 1px solid #1d2839; border-radius: 8px; background: #1d2839; color: #edbd46; box-shadow: 0 5px 15px rgba(24,33,48,.12); }
.welcome-support-trigger i { font-size: 21px; }
.welcome-support-trigger span { color: #fff; writing-mode: vertical-rl; font-size: 10px; letter-spacing: 2px; }

.welcome-nav-actions { display: flex; align-items: center; gap: 20px; }
.welcome-language { padding: 6px 0; border: 0; background: transparent; color: #64748b; font-family: inherit; font-size: 12px; cursor: pointer; }
.welcome-support-rail { width: 46px; top: auto; right: 20px; bottom: 28px; transform: none; }
.welcome-support-rail.is-open { width: 46px; }
.welcome-support-trigger, .welcome-support-rail.is-open .welcome-support-trigger { display: flex; cursor: pointer; }
.welcome-support-panel { position: absolute; width: 235px; right: 60px; bottom: 0; max-height: calc(100dvh - 140px); }
.welcome-support-panel button { cursor: pointer; font-family: inherit; }
.welcome-support-rail .welcome-support-title { background: #0f172a; }
.welcome-support-trigger { background: #0f172a; border-color: #0f172a; }
.welcome-support-trigger i { color: #f4b400; }
.welcome-support-panel .welcome-support-body { padding: 0 15px; }
.welcome-support-panel .support-number { margin: 8px 0 4px; }
.welcome-support-panel .welcome-support-hint { margin: 13px 0 17px; }
.welcome-support-panel .welcome-contact-note { margin: 0 0 14px; }
.welcome-page a:focus-visible, .welcome-page button:focus-visible { outline: 2px solid #f4b400; outline-offset: 4px; }
@media (max-width: 768px) {
  .welcome-nav-actions { gap: 10px; }
  .welcome-support-rail { right: 12px; bottom: 22px; }
  .welcome-support-trigger { height: 63px; padding: 8px 4px; gap: 3px; }
  .welcome-support-trigger span { writing-mode: horizontal-tb; letter-spacing: 0; }
  .welcome-support-panel { right: 0; bottom: 76px; max-height: calc(100dvh - 230px); }
}
@media (max-width: 380px) {
  header { padding-left: 4%; padding-right: 4%; }
  .logo-icon { width: 40px; height: 40px; font-size: 28px; }
  .logo-box { gap: 8px; }
  .logo-text { font-size: 20px; }
  .welcome-nav-actions { gap: 8px; }
  .welcome-language { font-size: 10px; }
  .nav-btn { padding-left: 16px; padding-right: 16px; }
}
@media (prefers-reduced-motion: reduce) { .welcome-page * { transition: none !important; } }

/* Motion is layered onto the reference layout without changing its geometry. */
.welcome-scroll-progress { position: fixed; top: 0; left: 0; z-index: 200; width: 100%; height: 3px; background: linear-gradient(90deg,#f4b400,#ffd971,#f4b400); transform: scaleX(var(--scroll-progress,0)); transform-origin: left; pointer-events: none; }
.fx-ready .hero::before { animation: welcome-orbit 18s ease-in-out infinite alternate; }
.fx-ready .hero h1 { animation: welcome-hero-in .8s cubic-bezier(.2,.7,.2,1) both; }
.fx-ready .hero .sub { animation: welcome-hero-in .8s .12s cubic-bezier(.2,.7,.2,1) both; }
.fx-ready .hero-cta { animation: welcome-hero-in .8s .24s cubic-bezier(.2,.7,.2,1) both; }
.fx-ready .hero h1 span { background: linear-gradient(110deg,#f4b400 20%,#ffd979 40%,#f4b400 60%); background-size: 240% 100%; -webkit-background-clip: text; background-clip: text; color: transparent; animation: welcome-gold-flow 7s ease-in-out infinite; }
.fx-ready .waybill-img { position: relative; isolation: isolate; overflow: hidden; }
.parcel-float { position: relative; z-index: 1; display: inline-block; }
.fx-ready .parcel-float { animation: welcome-parcel-float 5s ease-in-out infinite; filter: drop-shadow(0 14px 12px rgba(151,113,26,.16)); }
.fx-ready .waybill-img::before { content: ''; position: absolute; z-index: 0; width: 160px; height: 160px; border: 1px dashed rgba(194,151,47,.35); border-radius: 50%; transform: rotateX(60deg) rotateZ(-25deg); animation: welcome-package-ring 12s linear infinite; }
.fx-ready .waybill-img::after { content: ''; position: absolute; z-index: -1; width: 210px; height: 210px; background: radial-gradient(circle,rgba(244,180,0,.13),transparent 70%); }
.fx-ready .fx-pending { opacity: 0; }
.fx-ready .fx-visible { animation: welcome-section-in .7s cubic-bezier(.2,.7,.2,1) both; }
.fx-interactive { position: relative; isolation: isolate; }
.fx-interactive::after { content: ''; position: absolute; z-index: -1; inset: 0; border-radius: inherit; background: radial-gradient(350px circle at var(--pointer-x,50%) var(--pointer-y,50%),rgba(244,180,0,.12),transparent 70%); opacity: 0; transition: opacity .3s; pointer-events: none; }
.fx-interactive.fx-pointer-active::after { opacity: 1; }
.feat-visual { transition: transform .25s ease-out, box-shadow .3s; }
.fx-ready .feat-card.fx-pointer-active .feat-visual { transform: perspective(850px) rotateX(var(--tilt-x,0deg)) rotateY(var(--tilt-y,0deg)); box-shadow: 0 18px 40px rgba(151,113,26,.12); will-change: transform; }
.logo-icon { transition: transform .3s, box-shadow .3s; }
.logo-box:hover .logo-icon { transform: rotate(0) scale(1.06); box-shadow: 0 8px 24px rgba(244,180,0,.25); }
.btn-main, .btn-light { position: relative; isolation: isolate; overflow: hidden; }
.btn-main::after, .btn-light::after { content: ''; position: absolute; z-index: -1; top: -50%; left: -70%; width: 45%; height: 200%; background: linear-gradient(90deg,transparent,rgba(255,255,255,.45),transparent); transform: skewX(-20deg) translateX(0); pointer-events: none; }
.btn-main:hover::after, .btn-light:hover::after { transform: skewX(-20deg) translateX(500%); transition: transform .7s ease-out; }
.fx-paused *, .fx-paused *::before, .fx-paused *::after { animation-play-state: paused !important; }
@keyframes welcome-hero-in { from { opacity: 0; translate: 0 18px; } to { opacity: 1; translate: 0 0; } }
@keyframes welcome-section-in { from { opacity: 0; translate: 0 26px; } to { opacity: 1; translate: 0 0; } }
@keyframes welcome-orbit { from { transform: translate(0,0) scale(1); } to { transform: translate(35px,25px) scale(1.1); } }
@keyframes welcome-gold-flow { 0%,100% { background-position: 100% 50%; } 50% { background-position: 0 50%; } }
@keyframes welcome-parcel-float { 0%,100% { transform: translateY(0) rotate(-3deg); } 50% { transform: translateY(-10px) rotate(3deg); } }
@keyframes welcome-package-ring { from { transform: rotateX(60deg) rotateZ(-25deg); } to { transform: rotateX(60deg) rotateZ(335deg); } }
@media (max-width: 768px) {
  .fx-ready .hero::before { animation-duration: 24s; }
  .fx-ready .waybill-img::before { animation: none; }
}
@media (prefers-reduced-motion: reduce) {
  .welcome-page *, .welcome-page *::before, .welcome-page *::after { animation: none !important; transition: none !important; }
  .welcome-page .fx-pending { opacity: 1; }
  .welcome-page .hero h1 span { background: none; color: var(--yellow); }
  .welcome-page .feat-visual, .welcome-page .parcel-float { transform: none !important; }
  .welcome-page .btn-main::after, .welcome-page .btn-light::after { display: none; }
}

</style>
